// rsaPathTypes.js — Data model types, constants, and factory functions for the
// multi-path / multi-lane system. No React imports — fully pure.

// ─── ISD case constants ─────────────────────────────────────────────────────

export const ISD_CASES = {
  LEFT_TURN: "left-turn",
  RIGHT_TURN: "right-turn",
  F_CASE: "f-case",
  PEDESTRIAN: "pedestrian",
};

export const ISD_CASE_LABELS = {
  [ISD_CASES.LEFT_TURN]: "B1 (Left Turn)",
  [ISD_CASES.RIGHT_TURN]: "B2 (Right Turn)",
  [ISD_CASES.F_CASE]: "F (Left turn from major road)",
  [ISD_CASES.PEDESTRIAN]: "PD (Pedestrian)",
};

// ─── Lane type constants ─────────────────────────────────────────────────────

export const LANE_TYPES = {
  CENTER: "center",
  RIGHT_SHOULDER: "right-shoulder",
  LEFT_SHOULDER: "left-shoulder",
  CUSTOM: "custom",
};

export const LANE_TYPE_LABELS = {
  [LANE_TYPES.CENTER]: "Center Lane",
  [LANE_TYPES.RIGHT_SHOULDER]: "Right Shoulder",
  [LANE_TYPES.LEFT_SHOULDER]: "Left Shoulder",
  [LANE_TYPES.CUSTOM]: "Custom",
};

// ─── Factory functions ────────────────────────────────────────────────────────

/**
 * Create a new RsaPath object with sane defaults.
 * @param {Partial<RsaPath>} overrides
 * @returns {RsaPath}
 */
export function createPath(overrides = {}) {
  return {
    id: crypto.randomUUID(),
    label: "Route",
    waypoints: [],
    pathData: null,
    colorIndex: 0,
    visible: true,
    ...overrides,
  };
}

/**
 * Create a new RsaLane object linked to a parent path.
 *
 * Waypoint shape: each entry in `waypoints` is `{ lat, lng }`.
 * Waypoints may optionally carry `bridged: true` to indicate the point was
 * placed via bridge mode (used for renderer tint only; ignored on export).
 * Waypoints may also optionally carry `ai_anchor: true` to indicate the point
 * was suggested by Claude vision during the auto-trace AI-assist round
 * (used for renderer tint only; ignored on export).
 * Waypoints may also optionally carry `gap_after: true` to indicate the
 * auto-trace stopped at a gap right after this point (durable record of the
 * gap-continue flow; renderer-only, ignored on export).
 *
 * @param {string} pathId
 * @param {Partial<RsaLane>} overrides
 * @returns {RsaLane}
 */
export function createLane(pathId, overrides = {}) {
  return {
    id: crypto.randomUUID(),
    pathId,
    laneType: LANE_TYPES.CUSTOM,
    label: "Lane",
    waypoints: [],
    pathData: null,
    colorIndex: 1,
    visible: true,
    creationMode: "manual", // 'offset' | 'manual'
    sourcePathId: null,
    side: null, // 'left' | 'right'
    offsetFeet: null,
    directionMode: null, // 'same' | 'reverse'
    lanePosition: "LL", // 'LL' | 'CL' | 'RL'
    direction: "P", // 'N' | 'P'
    speedSegments: [], // [{ mileMark: number, speedMph: number }] — breakpoints along the lane
    reviewed: false, // user clicked "Done" on step 3 — persisted so the ready state survives refresh/tab switch
    ...overrides,
  };
}

/**
 * Create a new ISD intersection point.
 * @param {Partial<RsaIntersection>} overrides
 * @returns {RsaIntersection}
 */
export function createIntersection(overrides = {}) {
  return {
    id: crypto.randomUUID(),
    intersectionName: "", // INTERSECTION — name/number of intersection
    maneuverIndex: 1, // ID — ISD maneuver ID (auto-incremented)
    isdCase: ISD_CASES.LEFT_TURN, // CASE — left turn, right turn, F case
    oppositePoint: null, // OPPOSITEP { insertionPoint: {lat, lng}, azimuth, arrowLengthFeet } — F case only (cyan block)
    rsaPathId: null, // RSAPATH — references a path.id
    secondaryRoute: "", // Secondary route — minor road name (reports)
    insertionPoint: null, // {lat, lng} from map click
    azimuth: 0, // degrees clockwise from north
    arrowLengthFeet: 20, // length of azimuth arrow, set by drag
    visible: true,
    ...overrides,
  };
}

/**
 * Initial reducer state.
 * @returns {RsaPathsState}
 */
export function createInitialState() {
  return {
    paths: [],
    lanes: [],
    intersections: [],
    xmlToJsonMap: {},
    // Per-project LRS state override ('AL' | 'MA' | ...). null means "let
    // lrsGate auto-detect from this project's geometry".
    lrsState: null,
    activePathId: null,
    activeLaneId: null,
    activeIntersectionId: null,
    activeEntityType: "path", // 'path' | 'lane'
    laneEditMode: false,
    speedEditMode: false,
    selectedLaneWaypointIndices: [],
  };
}
